import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import {
  Bell, BellOff, MessageSquare, Mail, XCircle, FileEdit,
  CheckCircle2, ArrowUpRight, Inbox,
} from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import { Popover, IconButton, Spinner } from "../../design-system";

const ICON_BY_HINT = {
  'contact-pending':   MessageSquare,
  'contact-failed':    XCircle,
  'article-draft':     FileEdit,
  'article-published': CheckCircle2,
  'subscriber':        Mail,
};

function relativeTime(date) {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now - d;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

class AdminNotifications extends Component {
  state = {
    loading: false,
    items: [],
    unreadCount: 0,
    // Snapshot des keys non lues au moment de l'ouverture du popover, pour
    // que les pastilles « nouveau » restent visibles pendant cette session
    // même après le mark-as-read côté serveur.
    sessionUnreadKeys: new Set(),
    open: false,
  };

  componentDidMount() {
    this.load();
    this.interval = setInterval(this.load, 60000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  ensureAuth = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
  };

  load = async () => {
    try {
      this.setState({ loading: true });
      this.ensureAuth();
      const res = await axios.get("/api/notifications");
      const { items = [], unreadCount = 0 } = res.data || {};
      this.setState({ items, unreadCount, loading: false });
    } catch (error) {
      console.error("Error loading notifications:", error);
      this.setState({ loading: false });
    }
  };

  markRead = async (keys) => {
    if (!keys || keys.length === 0) return;
    try {
      this.ensureAuth();
      await axios.post("/api/notifications/read", { keys });
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  handleOpenChange = async (isOpen) => {
    if (isOpen) {
      // Snapshot des unread pour préserver visuellement les pastilles « nouveau »
      const unreadKeys = new Set(
        this.state.items.filter((it) => !it.isRead).map((it) => it.key)
      );
      this.setState({ open: true, sessionUnreadKeys: unreadKeys });

      // Auto-mark serveur (descend le badge à 0 et persiste pour multi-device)
      if (unreadKeys.size > 0) {
        await this.markRead(Array.from(unreadKeys));
        // Met à jour l'état local pour refléter le mark-as-read
        this.setState((s) => ({
          unreadCount: 0,
          items: s.items.map((it) => (
            unreadKeys.has(it.key) ? { ...it, isRead: true } : it
          )),
        }));
      }
    } else {
      this.setState({ open: false });
    }
  };

  handleItemClick = (item) => {
    // Si déjà lu, ne rien faire (déjà marqué à l'ouverture)
    if (item.isRead) return;
    this.markRead([item.key]);
    this.setState((s) => ({
      unreadCount: Math.max(0, s.unreadCount - 1),
      items: s.items.map((it) => (it.key === item.key ? { ...it, isRead: true } : it)),
    }));
  };

  render() {
    const { items, loading, unreadCount, sessionUnreadKeys, open } = this.state;

    return (
      <Popover
        side="bottom"
        align="end"
        onOpenChange={this.handleOpenChange}
        trigger={
          <span className="relative inline-block">
            <IconButton
              icon={<Bell strokeWidth={1.8} />}
              aria-label={unreadCount > 0 ? `Notifications (${unreadCount} non lues)` : "Notifications"}
              title="Notifications"
            />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-cream text-[10px] font-bold flex items-center justify-center pointer-events-none">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </span>
        }
        className="!min-w-[360px] !w-[360px] !p-0"
      >
        {({ close }) => (
          <div className="w-full max-h-[480px] flex flex-col">
            <header className="flex items-center justify-between px-3 py-2.5 border-b border-border">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-text" strokeWidth={1.8} />
                <span className="text-sm font-bold">Notifications</span>
                {unreadCount > 0 && (
                  <span className="text-[10.5px] px-1.5 py-0.5 rounded-full bg-danger-bg text-danger font-mono font-semibold">
                    {unreadCount} nouvelles
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    await this.markRead(this.state.items.filter((it) => !it.isRead).map((it) => it.key));
                    this.setState((s) => ({
                      unreadCount: 0,
                      items: s.items.map((it) => ({ ...it, isRead: true })),
                    }));
                  }}
                  className="text-[11px] text-mute hover:text-text font-medium"
                >
                  Tout marquer comme lu
                </button>
              )}
            </header>

            <div className="flex-1 overflow-y-auto">
              {loading && items.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-mute"><Spinner size="sm" /></div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center text-mute text-sm px-4">
                  <BellOff className="w-6 h-6 mb-2 text-mute-2" strokeWidth={1.6} />
                  Aucune notification pour le moment.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((it) => {
                    const Icon = ICON_BY_HINT[it.iconHint] || Inbox;
                    // Tant que le popover est ouvert, on affiche la pastille
                    // « nouveau » pour les items qui étaient unread à l'ouverture
                    // (même s'ils sont marqués lu côté serveur entre-temps).
                    const showDot = open
                      ? sessionUnreadKeys.has(it.key)
                      : !it.isRead;
                    const toneCls = it.kind === "error" ? "text-danger bg-danger-bg"
                      : it.kind === "success" ? "text-success bg-success-bg"
                      : "text-info bg-info-bg";
                    return (
                      <li key={it.key}>
                        <Link
                          to={it.to}
                          onClick={() => { this.handleItemClick(it); close(); }}
                          className="flex items-start gap-3 px-3 py-2.5 hover:bg-bg-2 transition-colors"
                        >
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${toneCls}`}>
                            <Icon className="w-4 h-4" strokeWidth={1.8} />
                          </div>
                          <div className="flex-1 min-w-0 leading-tight">
                            <div className="flex items-baseline gap-2">
                              <span className="text-[13px] font-semibold text-text">{it.title}</span>
                              {showDot && <span className="w-1.5 h-1.5 rounded-full bg-danger shrink-0" />}
                            </div>
                            <div className="text-[12px] text-mute truncate mt-0.5">{it.description}</div>
                            <div className="text-[10.5px] text-mute-2 font-mono mt-1">{relativeTime(it.date)}</div>
                          </div>
                          <ArrowUpRight className="w-3.5 h-3.5 text-mute-2 shrink-0 mt-0.5" strokeWidth={1.8} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <footer className="px-3 py-2 border-t border-border bg-bg-2/40 flex items-center justify-between text-[11px]">
              <span className="text-mute">Rafraîchi toutes les 60 s · synchronisé serveur</span>
              <button
                type="button"
                onClick={() => { this.load(); }}
                className="text-mute hover:text-text font-medium"
              >
                Actualiser
              </button>
            </footer>
          </div>
        )}
      </Popover>
    );
  }
}

export default withRouter(AdminNotifications);
